import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import _ from 'underscore';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import YesNoModal from 'components/Modals/yesNoModal';
import InformationModal from 'components/Modals/informationModal';
import TabSelector from 'components/TabSelector/TabSelector';
import { DistributionChart } from 'components/DistributionChart/DistributionChart';
import { categories } from 'constants/tests';
import { Checkbox } from 'components/Checkbox/Checkbox';
import getComputedDescription from 'functions/getComputedDescription';
import { useAppContext } from 'hooks/useContext';
import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import {
  SelectTest,
  TestsList,
  SelectedTestsList,
  SelectTestTabContent,
  SelectedTestButton,
  SelectedTestsTableContainer,
  TableHeader,
  TableRow,
  TableCell,
  ArrowUp,
  ArrowDown,
  TableActionButtonContainer,
  TableActionButton,
  TableCellContent,
  Note,
  PriceNote,
  InformationIcon,
  NoteContainer,
  XIcon,
  Block,
  DropdownButton,
  DropdownMenu,
  Dropdown,
  Divider,
  DropdownItem,
  CustomTimeLimit,
  NewBadge,
  ErrorMessage,
  ModalContentTitle,
  QuestionIcon,
  ModalContentContainer,
  LockIcon,
  PageActionContainer,
  TestsPackagesList,
  DescriptionContainer,
} from './styles';

const candidateChoosesTest = {
  _id: 'isCandidateChoosesEnabled',
  name: 'Candidate Chooses',
};

const SelectTestSection = forwardRef(
  (
    {
      tests,
      availableLanguages,
      adaptiveTestInfo,
      isDemoCompany,
      toggleTour,
      selectedTests,
      setSelectedTests,
      hasActiveSubscription,
    },
    _ref
  ) => {
    const { language } = useAppContext();
    const { company } = useSelector((state) => state.auth);
    const { isPremiumTestTrialActivated } = useSelector((state) => state.SendTestPageReducer);

    const { activatePremiumTestsTrial } = useActions(actions);

    const [freeCodeTests, setFreeCodeTests] = useState([]);
    const [isFreeCodeNameAscending, setIsFreeCodeNameAscending] = useState(true);
    const [isFreeCodeDifficultyAscending, setIsFreeCodeDifficultyAscending] = useState(true);
    const [freeCodePremiumTests, setFreeCodePremiumTests] = useState([]);
    const [isFreeCodePremiumNameAscending, setIsFreeCodePremiumNameAscending] = useState(true);
    const [isFreeCodePremiumDifficultyAscending, setIsFreeCodePremiumDifficultyAscending] = useState(true);
    const [multiChoiceTests, setMultiChoiceTests] = useState([]);
    const [candidateChoosesTests, setCandidateChoosesTests] = useState([]);
    const [isMultiChoiceNameAscending, setIsMultiChoiceNameAscending] = useState(true);
    const [generalTests, setGeneralTests] = useState([]);
    const [testsPackages, setTestsPackages] = useState([]);
    const [isGeneralNameAscending, setIsGeneralNameAscending] = useState(true);
    const [isPackageInfoModalOpen, setIsPackageInfoModalOpen] = useState(false);
    const [isInfoOnAdaptiveModalOpen, setIsInfoOnAdaptiveModalOpen] = useState(false);
    const [isInfoOnPremiumTestsModalOpen, setIsInfoOnPremiumTestsModalOpen] = useState(false);
    const [showPremiumTestsActivateConfirmationModal, setShowPremiumTestsActivateConfirmationModal] = useState(false);
    const [showAddToPlanModal, setShowAddToPlanModal] = useState(false);
    const [testInfoModal, setTestInfoModal] = useState({});
    const [testCategoriesData, setTestCategoriesData] = useState([
      { categoryName: 'Code', hasNewTest: false, hasCostMultiple: false },
      { categoryName: 'Multichoice', hasNewTest: false, hasCostMultiple: false },
      { categoryName: 'General Skills', hasNewTest: false, hasCostMultiple: false },
      { categoryName: 'Premium', isPremium: true, hasNewTest: false, hasCostMultiple: false },
    ]);

    useEffect(() => {
      if (isPremiumTestTrialActivated) toast.success('Freecode+ tests are activated');
    }, [isPremiumTestTrialActivated]);

    useEffect(() => {
      const sortedTests = tests.slice().sort((a, b) => {
        // Put demo tests first then sort by name
        return isDemoCompany && a.isDemoTest !== b.isDemoTest ? (a.isDemoTest ? -1 : 1) : a.name.localeCompare(b.name);
      });

      setFreeCodeTests(sortedTests.filter((test) => test.category === categories.CODE.category && !test.isPremium));
      setFreeCodePremiumTests(
        sortedTests.filter((test) => test.category === categories.CODE.category && test.isPremium)
      );
      const MCTests = sortedTests.filter((test) => test.category === categories.MULTICHOICE.category);
      setMultiChoiceTests(MCTests);
      setCandidateChoosesTests(_.indexBy(MCTests, '_id'));
      setGeneralTests(sortedTests.filter((test) => test.category === categories.GENERAL_SKILLS.category));
      setTestsPackages(sortedTests.filter((test) => test.category === 'Package'));
    }, [tests, isDemoCompany]);

    useEffect(() => {
      if (testsPackages?.length) {
        setTestCategoriesData([
          { categoryName: 'Code', hasNewTest: false, hasCostMultiple: false },
          { categoryName: 'Multichoice', hasNewTest: false, hasCostMultiple: false },
          { categoryName: 'General Skills', hasNewTest: false, hasCostMultiple: false },
          { categoryName: 'Premium', hasNewTest: false, hasCostMultiple: false, isPremium: true },
          { categoryName: 'Package', hasNewTest: false, hasCostMultiple: false },
        ]);
      }
    }, [testsPackages]);

    const sortFreeCodeTestsByName = useCallback(() => {
      const compare = (a, b) => {
        if (!isFreeCodeNameAscending) return a > b ? 1 : -1;
        return a > b ? -1 : 1;
      };
      setFreeCodeTests((oldTests) => [...oldTests].sort((a, b) => compare(a.name.toLowerCase(), b.name.toLowerCase())));
      setIsFreeCodeNameAscending(!isFreeCodeNameAscending);
      setIsFreeCodeDifficultyAscending(true);
    }, [isFreeCodeNameAscending]);

    const sortFreeCodeTestsByDifficulty = useCallback(() => {
      const difficultyScale = {
        Hard: isFreeCodeDifficultyAscending ? 3 : 1,
        Medium: 2,
        Easy: isFreeCodeDifficultyAscending ? 1 : 3,
      };
      setFreeCodeTests((oldTests) =>
        [...oldTests].sort((a, b) => {
          return (difficultyScale[b.difficulty] ?? 2) > (difficultyScale[a.difficulty] ?? 2) ? 1 : -1;
        })
      );
      setIsFreeCodeNameAscending(true);
      setIsFreeCodeDifficultyAscending(!isFreeCodeDifficultyAscending);
    }, [isFreeCodeDifficultyAscending]);

    const sortFreeCodePremiumTestsByName = useCallback(() => {
      const compare = (a, b) => {
        if (!isFreeCodePremiumNameAscending) return a > b ? 1 : -1;
        return a > b ? -1 : 1;
      };
      setFreeCodePremiumTests((oldTests) => {
        return [...oldTests].sort((a, b) => compare(a.name.toLowerCase(), b.name.toLowerCase()));
      });
      setIsFreeCodePremiumNameAscending(!isFreeCodePremiumNameAscending);
      setIsFreeCodePremiumDifficultyAscending(true);
    }, [isFreeCodePremiumNameAscending]);

    const sortFreeCodePremiumTestsByDifficulty = useCallback(() => {
      const difficultyScale = {
        Hard: isFreeCodePremiumDifficultyAscending ? 3 : 1,
        Medium: 2,
        Easy: isFreeCodePremiumDifficultyAscending ? 1 : 3,
      };
      setFreeCodePremiumTests((oldTests) =>
        [...oldTests].sort((a, b) => {
          return (difficultyScale[b.difficulty] ?? 2) > (difficultyScale[a.difficulty] ?? 2) ? 1 : -1;
        })
      );
      setIsFreeCodePremiumNameAscending(true);
      setIsFreeCodePremiumDifficultyAscending(!isFreeCodePremiumDifficultyAscending);
    }, [isFreeCodePremiumDifficultyAscending]);

    const sortMultiChoiceTestsByName = useCallback(() => {
      setMultiChoiceTests(
        multiChoiceTests.slice().sort((a, b) => {
          return isMultiChoiceNameAscending && b.name.toLowerCase() > a.name.toLowerCase() ? 1 : -1;
        })
      );
      setIsMultiChoiceNameAscending(!isMultiChoiceNameAscending);
    }, [isMultiChoiceNameAscending, multiChoiceTests]);

    const sortGeneralTestsByName = useCallback(() => {
      setGeneralTests(
        generalTests.slice().sort((a, b) => {
          return isGeneralNameAscending && b.name.toLowerCase() > a.name.toLowerCase() ? 1 : -1;
        })
      );
      setIsGeneralNameAscending(!isGeneralNameAscending);
    }, [generalTests, isGeneralNameAscending]);

    const testIsSelected = useCallback(
      (selectedTest) => selectedTests.some((test) => test._id === selectedTest._id),
      [selectedTests]
    );

    const toggleTestInSelectedTestList = useCallback(
      (test) => {
        let updatedList = [];
        if (testIsSelected(test)) {
          updatedList = [...selectedTests];
          updatedList = updatedList.filter((testItem) => testItem._id !== test._id);
        } else {
          let newTest = { ...test };
          if (test.isDemoTest) newTest.duration = 10;
          newTest.selectedTime = test.duration ?? 50;
          if (test.category === categories.CODE.category) {
            newTest.languages = _.mapObject(availableLanguages[test.executionVersion], () => true);
          }
          if (test._id === 'isCandidateChoosesEnabled')
            newTest.selectedMCChosenTests = _.mapObject(candidateChoosesTests, () => true);
          updatedList = [...selectedTests, newTest];
        }
        setSelectedTests(updatedList);
      },
      [availableLanguages, selectedTests, setSelectedTests, testIsSelected, candidateChoosesTests]
    );

    useEffect(() => {
      setSelectedTests((prevSelectedTests) => {
        const updatedTests = prevSelectedTests.map((test) => {
          if (test._id === 'isCandidateChoosesEnabled' && !_.keys(test.selectedMCChosenTests).length) {
            test.selectedMCChosenTests = _.mapObject(candidateChoosesTests, () => true);
          }
          return test;
        });
        return updatedTests;
      });
    }, [setSelectedTests, candidateChoosesTests]);

    const setTestDuration = (testIndex, newDuration) => {
      let updatedList = selectedTests.map((test, index) => {
        if (testIndex === index) return { ...test, selectedTime: newDuration };
        return test;
      });
      setSelectedTests(updatedList);
    };

    const changeTestSelectedLanguage = (index, languageKey) => {
      let updatedList = [...selectedTests];
      updatedList[index].languages[languageKey] = !updatedList[index].languages[languageKey];
      setSelectedTests(updatedList);
    };

    const changeAllTestSelectedLanguage = (index, allSelected) => {
      let updatedList = [...selectedTests];
      _.each(updatedList[index].languages, (val, key) => {
        updatedList[index].languages[key] = !allSelected;
      });
      setSelectedTests(updatedList);
    };

    const changeCandidateChoosesSelectedTest = (index, testId) => {
      let updatedList = [...selectedTests];
      updatedList[index].selectedMCChosenTests[testId] = !updatedList[index].selectedMCChosenTests[testId];
      setSelectedTests(updatedList);
    };

    const changeAllCandidateChoosesSelectedTest = (index, allSelected) => {
      let updatedList = [...selectedTests];
      _.each(updatedList[index].selectedMCChosenTests, (val, key) => {
        updatedList[index].selectedMCChosenTests[key] = !allSelected;
      });
      setSelectedTests(updatedList);
    };

    const renderArrow = (isUp) => {
      return isUp ? <ArrowUp /> : <ArrowDown />;
    };

    const renderInfoOnAdaptiveModal = () => {
      if (isInfoOnAdaptiveModalOpen)
        return (
          <InformationModal
            title="Info on adaptive tests"
            textHTML={adaptiveTestInfo}
            closeModal={() => setIsInfoOnAdaptiveModalOpen(false)}
          />
        );
    };

    const renderInfoOnPremiumTests = () => {
      if (isInfoOnPremiumTestsModalOpen)
        return (
          <InformationModal
            title="Info on Freecode+ tests"
            textHTML={'Freecode+ is a set of premium tests, which are more role and technology specific'}
            closeModal={() => setIsInfoOnPremiumTestsModalOpen(false)}
          />
        );
    };

    const renderInfoOnTestsPackagesModal = () => {
      if (isPackageInfoModalOpen)
        return (
          <InformationModal
            title="Info on package tests"
            textHTML="You can choose the tests package and it will send the mentioned tests to candidate"
            closeModal={() => setIsPackageInfoModalOpen(false)}
          />
        );
    };

    const renderTestInfoHTML = () => {
      return (
        <>
          <ModalContentTitle>Distribution</ModalContentTitle>
          <DistributionChart test={testInfoModal} forcedHeight="250px" />
          {testInfoModal.description && (
            <DescriptionContainer>
              <ModalContentTitle>Test description</ModalContentTitle>
              <ModalContentContainer
                dangerouslySetInnerHTML={{
                  __html: testInfoModal.description,
                }}
              />
            </DescriptionContainer>
          )}
          {testInfoModal.category === categories.CODE.category && testInfoModal.candidateDescription !== '' && (
            <>
              <ModalContentTitle>Candidate description</ModalContentTitle>
              <ModalContentContainer
                dangerouslySetInnerHTML={{
                  __html: getComputedDescription(
                    testInfoModal.candidateDescription,
                    testInfoModal.category,
                    'python',
                    0,
                    language,
                    false
                  ),
                }}
              />
            </>
          )}
        </>
      );
    };

    const testIsNew = useCallback(
      (test, isPremiumTab = false) => {
        if (
          test.createdAt === undefined ||
          test.custom === undefined ||
          test.custom ||
          diffDays(new Date(test.createdAt)) > 31 ||
          (test.isPremium && !isPremiumTab)
        ) {
          return false;
        }

        let categoryIndex;

        if (isPremiumTab) {
          categoryIndex = testCategoriesData.findIndex((category) => test.isPremium && category.isPremium);
        } else {
          categoryIndex = testCategoriesData.findIndex(
            (category) => category.categoryName === test.categoryName && !category.isPremium
          );
        }

        if (categoryIndex > -1 && !testCategoriesData[categoryIndex].hasNewTest) {
          let updatedData = [...testCategoriesData];
          updatedData[categoryIndex].hasNewTest = true;
          setTestCategoriesData(updatedData);
        }

        return true;
      },
      [testCategoriesData]
    );
    const testHasCostMultiple = useCallback(
      (test) => {
        if (test.price === 100) return false;

        let categoryIndex = -1;
        if (test.isPremium)
          categoryIndex = testCategoriesData.findIndex((category) => test.isPremium && category.isPremium);
        else categoryIndex = testCategoriesData.findIndex((category) => category.categoryName === test.categoryName);

        if (categoryIndex > -1 && !testCategoriesData[categoryIndex].hasCostMultiple) {
          let updatedData = [...testCategoriesData];
          updatedData[categoryIndex].hasCostMultiple = true;
          setTestCategoriesData(updatedData);
        }

        return true;
      },
      [testCategoriesData]
    );

    const renderFreeCodeTestsItems = useCallback(() => {
      let isSelected = false;
      return freeCodeTests?.map((test) => {
        isSelected = testIsSelected(test);
        const isDemo = isDemoCompany && !test.isDemoTest;
        return (
          <TableRow key={test._id} locked={isDemo}>
            <TableCell widthPercentage="40%">
              {test.name}
              {testIsNew(test) && <NewBadge>New</NewBadge>}
            </TableCell>
            <TableCell widthPercentage="60%">
              <TableCellContent>
                <Note>DIFFICULTY</Note>
                {test.difficulty ?? 'Mid-level'}
              </TableCellContent>
              <TableActionButtonContainer>
                <Note
                  isClickable
                  title="Information"
                  onClick={() => !isDemo && setTestInfoModal(test)}
                  disabled={isDemo}
                >
                  About the test <InformationIcon />
                </Note>
                <TableActionButton
                  id="selectTestButton"
                  isSelected={isSelected}
                  onClick={() => toggleTestInSelectedTestList(test)}
                  disabled={isDemo}
                >
                  {isSelected ? 'SELECTED' : 'SELECT TEST'}
                  {isDemo && <LockIcon title="Purchase a test to enable this option" />}
                  {testHasCostMultiple(test) && <PriceNote> (Costs {test.price / 100}x)</PriceNote>}
                </TableActionButton>
              </TableActionButtonContainer>
            </TableCell>
          </TableRow>
        );
      });
    }, [freeCodeTests, isDemoCompany, testIsNew, testHasCostMultiple, testIsSelected, toggleTestInSelectedTestList]);

    const renderFreeCodeTestsTab = useCallback(
      (hasCostMultiple) => {
        return (
          <SelectTestTabContent>
            <TableHeader isClickable widthPercentage="40%" onClick={() => sortFreeCodeTestsByName()}>
              Name{renderArrow(isFreeCodeNameAscending)}
            </TableHeader>
            <TableHeader isClickable mobileHide widthPercentage="40%" onClick={() => sortFreeCodeTestsByDifficulty()}>
              Difficulty {renderArrow(isFreeCodeDifficultyAscending)}
            </TableHeader>
            {hasCostMultiple && (
              <TableHeader widthPercentage="20%">
                <PriceNote>Note different credit value</PriceNote>
              </TableHeader>
            )}
            {renderFreeCodeTestsItems()}
          </SelectTestTabContent>
        );
      },
      [
        isFreeCodeDifficultyAscending,
        isFreeCodeNameAscending,
        renderFreeCodeTestsItems,
        sortFreeCodeTestsByDifficulty,
        sortFreeCodeTestsByName,
      ]
    );

    const renderFreeCodePremiumTestsItems = useCallback(() => {
      // companies that are flexible, ie limitless:false, should always be able to send freecode+ tests, the trial period allows them to send for free for 1 week
      // companies that are subscription, ie limitless:true, should not be able to send tests. It should be locked until zeb changes their settings. They can have a trial period that allows them to send for free for 1 week
      let isSelected = false;

      const isUnlimitedCompany = company.limitless;
      // If trialActivated has no value the trial isnt currently active or has been in the past.
      // trialActivated exists but is false means it has expired.
      const haveNotStartedTrial = company?.premium?.trialActivated === undefined;
      const isTrialActive = company?.premium?.trialActivated && new Date(company?.premium?.trialEndDate) > new Date();
      const endedTrialPeriod = _.has(company?.premium, 'trialActivated') && !company?.premium?.trialActivated;
      let isLocked;

      if (!isUnlimitedCompany) isLocked = false;
      if (isUnlimitedCompany && !isTrialActive) isLocked = true;

      const showTrialRow = haveNotStartedTrial && !hasActiveSubscription;

      let premiumTests = freeCodePremiumTests?.map((test) => {
        isSelected = testIsSelected(test);
        let showAddToPlan = isUnlimitedCompany && endedTrialPeriod;
        if (isUnlimitedCompany && !isTrialActive) {
          isLocked = !company?.premium?.premiumTests?.includes(String(test._id));
          showAddToPlan = !company?.premium?.premiumTests?.includes(String(test._id));
        }

        if (isDemoCompany) isLocked = true;

        return (
          <TableRow key={test._id} locked={isLocked && !showAddToPlan}>
            <TableCell widthPercentage="40%">
              {test.name}
              {testIsNew(test, true) && <NewBadge>New</NewBadge>}
            </TableCell>
            <TableCell widthPercentage="60%">
              <TableCellContent>
                <Note>DIFFICULTY</Note>
                {test.difficulty ?? 'Mid-level'}
              </TableCellContent>
              <TableActionButtonContainer>
                <Note
                  isClickable
                  title="Information"
                  onClick={() => !isDemoCompany && setTestInfoModal(test)}
                  disabled={isDemoCompany}
                >
                  About the test <InformationIcon />
                </Note>
                <TableActionButton
                  id="selectTestButton"
                  isSelected={isSelected}
                  onClick={() => (showAddToPlan ? setShowAddToPlanModal(true) : toggleTestInSelectedTestList(test))}
                  disabled={isLocked && !showAddToPlan}
                >
                  {showAddToPlan ? 'Add To Plan' : isSelected ? 'SELECTED' : 'SELECT TEST'}
                  {!showAddToPlan && isLocked && <LockIcon title={'Please contact sales to unlock the test'} />}
                  {!showAddToPlan && !isUnlimitedCompany && testHasCostMultiple(test) && (
                    <PriceNote> {isTrialActive ? 'Free during trial' : `(Costs ${test.price / 100}x)`}</PriceNote>
                  )}
                </TableActionButton>
              </TableActionButtonContainer>
            </TableCell>
          </TableRow>
        );
      });

      if (showTrialRow) {
        premiumTests.unshift(
          <TableRow key="premiumTestInfo" locked={isDemoCompany || endedTrialPeriod}>
            <TableCell widthPercentage="40%">{'Try for free for 1 week'}</TableCell>
            <TableCell widthPercentage="60%">
              <TableCellContent></TableCellContent>
              <TableActionButtonContainer>
                <Note
                  isClickable
                  title="Information"
                  onClick={() => !isDemoCompany && !endedTrialPeriod && setIsInfoOnPremiumTestsModalOpen(true)}
                >
                  About the Freecode+ tests <InformationIcon />
                </Note>
                <TableActionButton
                  id="selectTestButton"
                  isSelected={false}
                  onClick={() =>
                    !isDemoCompany && !endedTrialPeriod && setShowPremiumTestsActivateConfirmationModal(true)
                  }
                  disabled={isDemoCompany || endedTrialPeriod}
                >
                  Activate {isDemoCompany && <LockIcon title={'Purchase a test to enable this option'} />}
                </TableActionButton>
              </TableActionButtonContainer>
            </TableCell>
          </TableRow>
        );
      }

      return premiumTests;
    }, [
      freeCodePremiumTests,
      isDemoCompany,
      company?.premium,
      company?.limitless,
      testIsNew,
      testIsSelected,
      toggleTestInSelectedTestList,
      testHasCostMultiple,
      hasActiveSubscription,
    ]);

    const renderFreecodePremiumTestsTab = useCallback(
      (hasCostMultiple) => {
        return (
          <SelectTestTabContent>
            <TableHeader isClickable widthPercentage="40%" onClick={() => sortFreeCodePremiumTestsByName()}>
              Name{renderArrow(isFreeCodePremiumNameAscending)}
            </TableHeader>
            <TableHeader
              isClickable
              mobileHide
              widthPercentage="40%"
              onClick={() => sortFreeCodePremiumTestsByDifficulty()}
            >
              Difficulty {renderArrow(isFreeCodePremiumDifficultyAscending)}
            </TableHeader>
            {hasCostMultiple && (
              <TableHeader widthPercentage="20%">
                <PriceNote>Note different credit value</PriceNote>
              </TableHeader>
            )}
            {renderFreeCodePremiumTestsItems()}
          </SelectTestTabContent>
        );
      },
      [
        isFreeCodePremiumDifficultyAscending,
        isFreeCodePremiumNameAscending,
        renderFreeCodePremiumTestsItems,
        sortFreeCodePremiumTestsByDifficulty,
        sortFreeCodePremiumTestsByName,
      ]
    );

    const renderTestItems = useCallback(
      (testList) => {
        let isSelected = false;
        return testList.map((test) => {
          isSelected = testIsSelected(test);
          const isDemo = isDemoCompany && !test.isDemoTest;
          return (
            <TableRow key={test._id} locked={isDemo}>
              <TableCell widthPercentage="40%">
                {test.name}
                {testIsNew(test) && <NewBadge>New</NewBadge>}
              </TableCell>
              <TableCell widthPercentage="60%">
                <TableCellContent>{!test.adaptiveTest && 'Non-'}Adaptive</TableCellContent>
                <TableActionButtonContainer>
                  <Note
                    isClickable
                    title="Information"
                    onClick={() => !isDemo && setTestInfoModal(test)}
                    disabled={isDemo}
                  >
                    About the test <InformationIcon />
                  </Note>
                  <TableActionButton
                    id="selectTestButton"
                    isSelected={isSelected}
                    onClick={() => toggleTestInSelectedTestList(test)}
                    disabled={isDemo}
                  >
                    {isSelected ? 'SELECTED' : 'SELECT TEST'}
                    {isDemo && <LockIcon title="Purchase a test to enable this option" />}
                    {testHasCostMultiple(test) && <PriceNote> (Costs {test.price / 100}x)</PriceNote>}
                  </TableActionButton>
                </TableActionButtonContainer>
              </TableCell>
            </TableRow>
          );
        });
      },
      [isDemoCompany, testIsNew, testHasCostMultiple, testIsSelected, toggleTestInSelectedTestList]
    );

    const renderMultiChoiceTestsTab = useCallback(
      (hasCostMultiple) => {
        let isCandidateChoosesTestSelected = testIsSelected(candidateChoosesTest);
        return (
          <SelectTestTabContent>
            <TableHeader isClickable widthPercentage="40%" onClick={() => sortMultiChoiceTestsByName()}>
              Name{renderArrow(isMultiChoiceNameAscending)}
            </TableHeader>
            <TableHeader
              isClickable
              mobileHide
              widthPercentage="40%"
              onClick={() => setIsInfoOnAdaptiveModalOpen(true)}
            >
              Type <InformationIcon />
            </TableHeader>
            {hasCostMultiple && (
              <TableHeader widthPercentage="20%">
                <PriceNote>Note different credit value</PriceNote>
              </TableHeader>
            )}
            <TableRow key={-1} locked={isDemoCompany}>
              <TableCell widthPercentage="40%">{candidateChoosesTest.name}</TableCell>
              <TableCell widthPercentage="60%">
                <TableCellContent></TableCellContent>
                <TableActionButtonContainer>
                  <Note
                    isClickable
                    title="Allow the candidate to choose which multichoice test to take"
                    disabled={isDemoCompany}
                  >
                    About the feature <InformationIcon />
                  </Note>
                  <TableActionButton
                    isSelected={isCandidateChoosesTestSelected}
                    onClick={() => toggleTestInSelectedTestList(candidateChoosesTest)}
                    disabled={isDemoCompany}
                  >
                    {isCandidateChoosesTestSelected ? 'SELECTED' : 'SELECT TEST'}
                    {isDemoCompany && <LockIcon title="Purchase a test to enable this option" />}
                  </TableActionButton>
                </TableActionButtonContainer>
              </TableCell>
            </TableRow>
            {renderTestItems(multiChoiceTests)}
          </SelectTestTabContent>
        );
      },
      [
        isDemoCompany,
        isMultiChoiceNameAscending,
        multiChoiceTests,
        renderTestItems,
        sortMultiChoiceTestsByName,
        testIsSelected,
        toggleTestInSelectedTestList,
      ]
    );

    const renderGeneralTestsTab = useCallback(
      (hasCostMultiple) => {
        return (
          <SelectTestTabContent>
            <TableHeader isClickable widthPercentage="40%" onClick={() => sortGeneralTestsByName()}>
              Name{renderArrow(isGeneralNameAscending)}
            </TableHeader>
            <TableHeader
              isClickable
              mobileHide
              widthPercentage="40%"
              onClick={() => setIsInfoOnAdaptiveModalOpen(true)}
            >
              Type <InformationIcon />
            </TableHeader>
            {hasCostMultiple && (
              <TableHeader widthPercentage="20%">
                <PriceNote>Note different credit value</PriceNote>
              </TableHeader>
            )}
            {renderTestItems(generalTests)}
          </SelectTestTabContent>
        );
      },
      [generalTests, isGeneralNameAscending, renderTestItems, sortGeneralTestsByName]
    );

    const renderPackages = useCallback(() => {
      return testsPackages.map((testsPackage) => {
        let isSelected = testIsSelected(testsPackage);
        return (
          <TableRow key={testsPackage.name}>
            <TableCell widthPercentage="40%">{testsPackage.name}</TableCell>
            <TableCell widthPercentage="60%">
              <TableCellContent>
                {testsPackage.tests.map((test) => (
                  <TestsPackagesList key={test._id}>- {test.name}</TestsPackagesList>
                ))}
              </TableCellContent>
              <TableActionButtonContainer>
                <TableActionButton
                  id="selectTestButton"
                  isSelected={isSelected}
                  onClick={() => toggleTestInSelectedTestList(testsPackage)}
                >
                  {isSelected ? 'SELECTED' : 'SELECT PACKAGE'}
                </TableActionButton>
              </TableActionButtonContainer>
            </TableCell>
          </TableRow>
        );
      });
    }, [testIsSelected, testsPackages, toggleTestInSelectedTestList]);

    const renderPackageTab = useCallback(() => {
      return (
        <SelectTestTabContent>
          <TableHeader isClickable widthPercentage="40%">
            Name
          </TableHeader>
          <TableHeader isClickable mobileHide widthPercentage="60%" onClick={() => setIsPackageInfoModalOpen(true)}>
            Tests <InformationIcon />
          </TableHeader>
          {renderPackages()}
        </SelectTestTabContent>
      );
    }, [renderPackages]);

    const diffDays = (date) => {
      return Math.round(new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    };

    const renderTestsTabs = useCallback(() => {
      let items = [];
      if (freeCodeTests.length)
        items.push({
          id: 'testCategories',
          label: renderCategoryLabelNewBadge('FREECODE', testCategoriesData[0].hasNewTest),
          component: renderFreeCodeTestsTab(testCategoriesData[0].hasCostMultiple),
        });

      if (freeCodePremiumTests.length)
        items.push({
          id: 'testCategories',
          label: renderCategoryLabelNewBadge('FREECODE+', testCategoriesData[3].hasNewTest),
          component: renderFreecodePremiumTestsTab(testCategoriesData[3].hasCostMultiple),
          isPremiumTab: true,
        });

      if (multiChoiceTests.length)
        items.push({
          id: 'testCategories',
          label: renderCategoryLabelNewBadge('MULTICHOICE', testCategoriesData[1].hasNewTest),
          component: renderMultiChoiceTestsTab(testCategoriesData[1].hasCostMultiple),
        });

      if (generalTests.length)
        items.push({
          id: 'testCategories',
          label: renderCategoryLabelNewBadge('GENERAL', testCategoriesData[2].hasNewTest),
          component: renderGeneralTestsTab(testCategoriesData[2].hasCostMultiple),
        });

      if (testsPackages.length)
        items.push({
          id: 'testCategories',
          label: renderCategoryLabelNewBadge('PACKAGE', testCategoriesData[4]?.hasNewTest),
          component: renderPackageTab(),
        });

      const premiumTabIndex = _.findIndex(items, (item) => item.isPremiumTab);

      return (
        <TabSelector
          key={`tab${items.length}`}
          items={items}
          externalActiveIndex={isPremiumTestTrialActivated ? premiumTabIndex : -1}
        ></TabSelector>
      );
    }, [
      testCategoriesData,
      renderFreeCodeTestsTab,
      renderMultiChoiceTestsTab,
      renderGeneralTestsTab,
      renderPackageTab,
      renderFreecodePremiumTestsTab,
      freeCodeTests.length,
      multiChoiceTests.length,
      generalTests.length,
      freeCodePremiumTests.length,
      testsPackages.length,
      isPremiumTestTrialActivated,
    ]);

    const renderCategoryLabelNewBadge = (label, hasNewTest) => {
      return (
        <>
          {label}
          {hasNewTest && <NewBadge hasPadding>NEW TEST</NewBadge>}
        </>
      );
    };

    const renderSelectedTestsTable = () => {
      return (
        <SelectTestTabContent autoHeight>
          <TableHeader>Chosen tests</TableHeader>
          <TableHeader></TableHeader>
          {renderSelectedTestsItems()}
        </SelectTestTabContent>
      );
    };

    const renderSelectedTestsItems = () => {
      if (selectedTests.length > 0) {
        return selectedTests.map((selectedTest, index) => {
          return (
            <TableRow key={selectedTest._id}>
              <TableCell widthPercentage={selectedTest.category === 'Package' ? '92%' : '30%'}>
                <Block>
                  {selectedTest.name} {selectedTest.category === 'Package' && ' Package'}
                  {renderAdaptiveTestNote(selectedTest)}
                </Block>
              </TableCell>
              {selectedTest.category !== 'Package' && (
                <>
                  <TableCell widthPercentage="30%" zeroPaddingLeft>
                    {renderTimeDropDown(selectedTest, index)}
                  </TableCell>
                  <TableCell widthPercentage="32%" zeroPaddingLeft verticalTop>
                    {selectedTest._id === 'isCandidateChoosesEnabled'
                      ? renderCandidateChoosesTestDropdown(selectedTest, index)
                      : renderProgrammingLanguageDropDown(selectedTest, index)}
                  </TableCell>
                </>
              )}
              <TableCell widthPercentage="8%">
                <TableActionButtonContainer widthPercentage="40%">
                  <XIcon onClick={() => toggleTestInSelectedTestList(selectedTest)} />
                </TableActionButtonContainer>
              </TableCell>
            </TableRow>
          );
        });
      } else
        return (
          <TableRow>
            <NoteContainer>
              <Note>You haven't selected any tests yet.</Note>
            </NoteContainer>
          </TableRow>
        );
    };

    const renderAdaptiveTestNote = (selectedTest) => {
      if (selectedTest.adaptiveTest) return <Note>Adaptive Test</Note>;
    };

    const renderTimeDropDown = (selectedTest, index) => {
      const normalDuration = selectedTest.duration ?? 50;
      const offset = normalDuration / 3;
      const ROUND_TO_NEAREST = 5.0;

      // 2/3 (66.67%) of normalDuration, rounded down to nearest multiple of ROUND_TO_NEAREST.
      const shortDuration = Math.floor((normalDuration - offset) / ROUND_TO_NEAREST) * ROUND_TO_NEAREST;
      // 4/3 (133.33%) of normalDuration, rounded up to nearest multiple of ROUND_TO_NEAREST.
      const longDuration = Math.ceil((normalDuration + offset) / ROUND_TO_NEAREST) * ROUND_TO_NEAREST;

      return (
        <Block>
          <Note>TIME LIMIT</Note>
          <Dropdown>
            <DropdownButton widthSize="96px" id="timeLimitDropdownlist" data-toggle="dropdown" disabled={isDemoCompany}>
              {selectedTest.selectedTime} min <ArrowDown />
            </DropdownButton>
            <DropdownMenu>
              <Note>Standard time limits</Note>
              <DropdownItem onClick={() => setTestDuration(index, shortDuration)}>{shortDuration} minutes</DropdownItem>
              <DropdownItem onClick={() => setTestDuration(index, normalDuration)}>
                {normalDuration} minutes (recommended)
              </DropdownItem>
              <DropdownItem onClick={() => setTestDuration(index, longDuration)}>{longDuration} minutes</DropdownItem>
              <Divider />
              <Note>Custom time limit</Note>
              <DropdownItem selected>
                <CustomTimeLimit
                  value={selectedTest.selectedTime}
                  onChange={(e) => setTestDuration(index, e.target.value)}
                ></CustomTimeLimit>
                minutes
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          {(!selectedTest.selectedTime || selectedTest.selectedTime < 5) && (
            <ErrorMessage small noMarginBottom>
              Time is not valid
            </ErrorMessage>
          )}
        </Block>
      );
    };

    const renderCandidateChoosesTestDropdown = (selectedTest, index) => {
      const selectedCount = _.filter(selectedTest.selectedMCChosenTests, (val) => val).length;
      const allSelected = _.keys(selectedTest.selectedMCChosenTests).length === selectedCount;
      return (
        <Block marginTop="2px">
          <Note fontSize="10px">ALLOWED TESTS</Note>
          <Dropdown>
            <DropdownButton widthSize="117px" data-toggle="dropdown">
              {selectedCount > 0 ? `${selectedCount} selected` : 'Choose'} <ArrowDown />
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={allSelected}
                  label={allSelected ? 'Unselect all' : 'Select all'}
                  onChange={() => changeAllCandidateChoosesSelectedTest(index, allSelected)}
                />
              </DropdownItem>
              {_.map(candidateChoosesTests, (val, key) => {
                return (
                  <DropdownItem key={key} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedTest.selectedMCChosenTests[key]}
                      label={candidateChoosesTests[key].name}
                      onChange={() => changeCandidateChoosesSelectedTest(index, key)}
                    />
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </Dropdown>
          {selectedCount === 0 && (
            <ErrorMessage small noMarginBottom>
              Please select test
            </ErrorMessage>
          )}
        </Block>
      );
    };

    const renderProgrammingLanguageDropDown = (selectedTest, index) => {
      if (selectedTest.category === categories.CODE.category) {
        const selectedCount = _.filter(selectedTest.languages, (val) => val).length;
        const allSelected = _.keys(selectedTest.languages).length === selectedCount;
        return (
          <Block marginTop="2px">
            <Note fontSize="10px">ALLOWED LANGUAGES</Note>
            <Dropdown>
              <DropdownButton widthSize="117px" data-toggle="dropdown">
                {selectedCount > 0 ? `${selectedCount} selected` : 'Choose'} <ArrowDown />
              </DropdownButton>
              <DropdownMenu dropdownRight>
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    label={allSelected ? 'Unselect all' : 'Select all'}
                    onChange={() => changeAllTestSelectedLanguage(index, allSelected)}
                  />
                </DropdownItem>
                {_.map(availableLanguages[selectedTest.executionVersion], (val, key) => {
                  return (
                    <DropdownItem
                      key={key}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        checked={selectedTest.languages[key]}
                        label={val.CurrentVersion}
                        onChange={() => {
                          changeTestSelectedLanguage(index, key);
                        }}
                      />
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </Dropdown>
            {selectedCount === 0 && (
              <ErrorMessage small noMarginBottom>
                Please select language
              </ErrorMessage>
            )}
          </Block>
        );
      }
    };

    return (
      <SelectTest>
        <PageActionContainer>
          {!isDemoCompany && (
            <SelectedTestButton href="/customTests" title="Create your own test with questions of your own liking">
              CREATE CUSTOMIZED TEST
            </SelectedTestButton>
          )}
          <SelectedTestButton width="30px" onClick={toggleTour}>
            <QuestionIcon />
          </SelectedTestButton>
        </PageActionContainer>
        <TestsList>{renderTestsTabs()}</TestsList>
        <SelectedTestsList>
          <SelectedTestsTableContainer id="selectedTestsTable">
            {renderSelectedTestsTable()}
          </SelectedTestsTableContainer>
        </SelectedTestsList>
        {renderInfoOnAdaptiveModal()}
        {renderInfoOnPremiumTests()}
        {renderInfoOnTestsPackagesModal()}
        {_.keys(testInfoModal).length > 0 && (
          <InformationModal
            title={testInfoModal.name}
            text={renderTestInfoHTML()}
            closeModal={() => setTestInfoModal({})}
          />
        )}
        {showPremiumTestsActivateConfirmationModal && (
          <YesNoModal
            modalInfo={{
              title: 'Activate Freecode+ Tests',
              body: 'Do you want to activate your Freecode+ trial?',
            }}
            confirm={(isConfirmed) => {
              if (isConfirmed) activatePremiumTestsTrial();
              setShowPremiumTestsActivateConfirmationModal(false);
            }}
          />
        )}
        {showAddToPlanModal && (
          <InformationModal
            title={'Add to Plan'}
            textHTML={
              'To activate freecode+ tests, go to <a href="/plans">plans</a> page and start a monthly subscription.'
            }
            closeModal={() => setShowAddToPlanModal(false)}
          />
        )}
      </SelectTest>
    );
  }
);

export default SelectTestSection;
